import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { newsCategories } from "@/mocks/categories";
import { newsArticles } from "@/mocks/articles";
import { liveFeed } from "@/mocks/live";
import type { NewsArticle } from "@/mocks/articles";

function Header() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === "/";
  const Brand = (
    <Link to="/" className="flex items-baseline gap-2">
      <span className="font-heading text-2xl font-black leading-none text-primary-600 md:text-3xl">
        吃瓜<span className="text-foreground-950">新闻</span>
      </span>
    </Link>
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const results: NewsArticle[] =
    query.trim().length > 0
      ? newsArticles
          .filter((a) => `${a.title}${a.summary}${a.tags.join("")}`.includes(query.trim()))
          .slice(0, 6)
      : [];

  return (
    <header className="sticky top-0 z-50">
      {/* 直播滚动公告条 */}
      <Link
        to="/live"
        className="flex items-center gap-2 bg-primary-600 px-4 py-2 text-background-50 hover:bg-primary-700 transition-colors"
        aria-label="前往直播滚动更新频道"
      >
        <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-background-50/15 px-2.5 py-0.5 text-xs font-semibold">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
          直播
        </span>
        <span className="relative flex-1 overflow-hidden whitespace-nowrap text-xs text-background-50/90">
          <span className="ticker-track inline-block">
            <span className="inline-flex animate-ticker">
              {liveFeed.slice(0, 6).map((item) => (
                <span key={item.id} className="inline-flex items-center">
                  <span className="mx-5 text-accent-200">·</span>
                  {item.text}
                </span>
              ))}
              {liveFeed.slice(0, 6).map((item) => (
                <span key={`dup-${item.id}`} className="inline-flex items-center" aria-hidden="true">
                  <span className="mx-5 text-accent-200">·</span>
                  {item.text}
                </span>
              ))}
            </span>
          </span>
        </span>
        <span className="hidden shrink-0 items-center gap-1 text-xs font-medium sm:flex">
          进入频道
          <i className="ri-arrow-right-line" />
        </span>
      </Link>

      {/* 主站头 */}
      <div className={`border-b border-background-200 transition-all ${scrolled ? "bg-background-50/95 shadow-card backdrop-blur" : "bg-background-50"}`}>
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-800 md:hidden"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开或关闭导航菜单"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-3-line"} text-lg`} />
            </button>
            {isHome ? <h1 className="flex items-center">{Brand}</h1> : <div className="flex items-center">{Brand}</div>}
            <span className="hidden border-l border-background-200 pl-3 text-xs leading-5 text-foreground-500 lg:block">
              今日快讯 · 早报晚报
              <br />
              全天滚动更新
            </span>
          </div>

          <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
            <Link
              to="/"
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
            >
              首页
            </Link>
            <Link
              to="/live"
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
            >
              直播
            </Link>
            <span className="mx-1 h-4 w-px bg-background-200" aria-hidden="true" />
            {newsCategories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="rounded-md px-2.5 py-2 text-sm text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-600"
              >
                {c.name}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div ref={searchRef} className="relative">
              <button
                type="button"
                onClick={() => setSearchOpen((v) => !v)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                aria-label="搜索新闻"
              >
                <i className="ri-search-line" />
              </button>
              {searchOpen && (
                <div className="absolute right-0 top-11 w-[min(84vw,340px)] overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-card">
                  <div className="border-b border-background-100 px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <i className="ri-search-line text-foreground-400" />
                      <input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="搜索要闻、栏目或关键词…"
                        className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                      />
                    </div>
                  </div>
                  <div className="max-h-80 overflow-y-auto p-1.5">
                    {results.length > 0 ? (
                      results.map((a) => (
                        <Link
                          key={a.id}
                          to={`/article/${a.slug}`}
                          className="flex items-start gap-2 rounded-md px-2 py-2 hover:bg-background-100"
                        >
                          <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                          <span className="text-sm leading-5 text-foreground-700">{a.title}</span>
                        </Link>
                      ))
                    ) : query.trim().length > 0 ? (
                      <p className="px-3 py-4 text-center text-sm text-foreground-400">未找到相关内容</p>
                    ) : (
                      <p className="px-3 py-4 text-center text-sm text-foreground-400">输入关键词开始搜索</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/about"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:block"
            >
              关于我们
            </Link>
          </div>
        </div>

        {/* 移动端菜单 */}
        {menuOpen && (
          <nav className="border-t border-background-100 bg-background-50 px-4 py-3 md:hidden" aria-label="移动端主导航">
            <div className="grid grid-cols-2 gap-1">
              <Link to="/" className="rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-800">
                首页
              </Link>
              <Link to="/live" className="rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-800">
                直播滚动
              </Link>
              {newsCategories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="rounded-md px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100"
                >
                  {c.name}快讯
                </Link>
              ))}
              <Link to="/about" className="rounded-md px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100">
                关于我们
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}

export default Header;